import React, { useState } from "react";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { Chip, IconButton, Menu, MenuItem, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";

import { CustomMeteo } from "src/types";
import { isDisabled } from "src/utils/permission.utils";
import { StickyTable } from "./StickyTable";
import { IsVisible } from "./permissions";
import { useMeteoDeleteMutation, useMeteoSetAsDefaultMutation } from "src/hooks/custom-meteo";

interface Props {
  className?: string;
  data: CustomMeteo[];
}

export function MeteoTable(props: Props) {
  const { t } = useTranslation();
  const { data } = props;

  return render();

  function render() {
    return (
      <StickyTable className={props.className}>
        <TableHead>
          <TableRow>
            <TableCell>{t("label.name")}</TableCell>
            <TableCell>{t("label.type")}</TableCell>
            <TableCell>{t("label.linkedCases")}</TableCell>
            <TableCell>{t("label.status")}</TableCell>
            <TableCell>{t("label.created")}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.id}>
              <TableCell component="th" scope="row">
                {item.name}
              </TableCell>
              <TableCell>{t(`label.customMeteoType.${item.customMeteoType}`)}</TableCell>
              <TableCell>{item.linkedCases.length}</TableCell>
              <TableCell>{t(`label.importStatus.${item.importStatus}`)}</TableCell>
              <TableCell>{t("label.dateCreated", { date: item.dateCreated })}</TableCell>
              <TableCell align="right">
                {item.defaultValue && <Chip label="Default" color="primary" variant="outlined" />}
              </TableCell>
              <TableCell align="right">
                <RowActions data={item} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </StickyTable>
    );
  }
}

function RowActions(props: { data: CustomMeteo }) {
  const {
    data,
    data: { permissions },
  } = props;
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const setDefaultMutation = useMeteoSetAsDefaultMutation(data.id);
  const deleteMutation = useMeteoDeleteMutation(data.id);

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleSetAsDefault() {
    setDefaultMutation.mutate();
    handleClose();
  }

  function handleDelete() {
    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deleteMeteo"),
    })
      .then(() => deleteMutation.mutate())
      .finally(() => handleClose());
  }

  return render();

  function render() {
    return (
      <IsVisible permission={[permissions.delete, permissions.setDefault, permissions.update, permissions.upload]}>
        <IconButton onClick={handleClick} size="large">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <IsVisible permission={permissions.update}>
            <MenuItem
              disabled
              // TODO: enable next prop when edit func enters the game
              // disabled={isDisabled(permissions.update)}
            >
              {t("label.edit")}
            </MenuItem>
          </IsVisible>
          <IsVisible permission={permissions.delete}>
            <MenuItem onClick={handleDelete} disabled={isDisabled(permissions.delete)}>
              {t("label.delete")}
            </MenuItem>
          </IsVisible>
          <IsVisible permission={permissions.setDefault}>
            <MenuItem onClick={handleSetAsDefault} disabled={isDisabled(permissions.setDefault)}>
              {t("label.setAsDefault")}
            </MenuItem>
          </IsVisible>
          {data.downloadUrl && (
            <MenuItem component="a" href={data.downloadUrl}>
              {t("label.download")}
            </MenuItem>
          )}
        </Menu>
      </IsVisible>
    );
  }
}
